import * as React from 'react';
import {Button, Typography} from 'onboarding-components';
import {useState} from 'react';

import {Header} from 'common/onboarding/immersive-onboarding/components/header';
import {Content} from 'common/onboarding/immersive-onboarding/components/content';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding/types';
import {IMMERSIVE_ONBOARDING_URL_PARAM, INDUSTRY_EVENTS} from 'common/onboarding/immersive-onboarding/constants';
import {ScrollableLink} from 'common/onboarding/immersive-onboarding/components/scroll-manager';
import {ScrollableElement, scrollToStep} from 'common/onboarding/immersive-onboarding/components/scroll-manager';
import {TileButton} from 'common/onboarding/components/tile-button';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {TemplateType} from 'common/report/dashboards/types';
import {track, TrackingEvent, TrackingProperty} from 'common/onboarding/immersive-onboarding/tracking';

import styles from './explore.module.styl';

export const Explore = () => {
  const {isIntegrated, modalManager, setupGuideInfo} = React.useContext(SetupGuideContext);

  const [selectedTemplate, setSelectedTemplate] = useState(TemplateType.KPI);

  const [showExploreTemplates, setShowExploreTemplates] = useState(false);

  const templateAssets = {
    [TemplateType.KPI]: IMMERSIVE_ASSETS.companyKpisTemplate,
    [TemplateType.FEATURE_LAUNCH]: IMMERSIVE_ASSETS.featureLaunchTemplate,
    [TemplateType.LIFECYCLE_COHORT_ANALYSIS]: IMMERSIVE_ASSETS.lifecycleCohortTemplate,
    [TemplateType.MARKETING_KPI]: IMMERSIVE_ASSETS.marketingKpisTemplate,
    [TemplateType.WEB_ANALYTICS]: IMMERSIVE_ASSETS.webAnalyticsTemplate,
  };

  const TemplateButton = ({templateName, isSelected, onSelect}) => (
    <TileButton
      className={styles.ctaButton}
      onClick={() => {
        track(TrackingEvent.TemplateTabClick, {[TrackingProperty.Template]: templateName});
        onSelect(templateName);
      }}
      selected={isSelected}
    >
      {templateName}
    </TileButton>
  );

  const templates = INDUSTRY_EVENTS[setupGuideInfo?.industry ?? `b2b`][`templates`];

  const handleTemplateSelection = () => {
    const url = new URL(
      getReportUrl(`boards`, {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      }),
      window.location.origin,
    );
    url.searchParams.delete(IMMERSIVE_ONBOARDING_URL_PARAM);
    window.open(
      `${url.toString()}?show-template-selector=true&selected-template=${encodeURIComponent(selectedTemplate)}`,
      `_blank`,
    );
  };

  return (
    <>
      <Header title={`Explore product insights`} step={`wrap up`} imgSrc={IMMERSIVE_ASSETS.goggles} duration={1} />
      <Content>
        <div className={styles.explore}>
          <div className={styles.header}>
            <div className={styles.heroImage}>
              <img
                src={isIntegrated ? IMMERSIVE_ASSETS.exploreCongratulations : IMMERSIVE_ASSETS.exploreNoEvents}
                width='250'
                height='208'
              />
            </div>
            <div className={styles.titleContainer}>
              <Typography className={styles.title} variant='headingMd' weight='bold'>
                {isIntegrated ? `Congratulations!` : `No events detected`}
              </Typography>

              <Typography className={styles.description} variant='bodySm' weight='regular'>
                {isIntegrated
                  ? `We received your data, and you’re ready to start learning. The templates below are a great place to start.`
                  : `Activate the templates below by sending Mixpanel a couple events`}
              </Typography>
            </div>
            <div>
              {isIntegrated ? (
                <Button
                  variant={`solid`}
                  onClick={() => {
                    scrollToStep(`exploreTemplates`);
                    setShowExploreTemplates(true);
                  }}
                >
                  {`Start Building`}
                </Button>
              ) : (
                <ScrollableLink targetStep={Step.Events}>
                  <Button variant={`transparent`}>{`Return to Setup`}</Button>
                </ScrollableLink>
              )}
            </div>
          </div>
          <ScrollableElement name={`exploreTemplates`} active={showExploreTemplates}>
            <Typography variant='bodyMd' weight='bold'>{`We recommend starting with one or more of these templates${
              setupGuideInfo.industry ? ` for ` + INDUSTRY_EVENTS[setupGuideInfo.industry].name : ``
            }`}</Typography>
            <div className={styles.useTemplate}>
              <Typography variant='bodySm'>{`Templates are a fast and easy way to uncover insights about your users and product`}</Typography>
            </div>
            <div className={styles.useTemplateCta}>
              <div className={styles.ctaButtons}>
                {templates.map((templateName) => (
                  <TemplateButton
                    key={templateName}
                    templateName={templateName}
                    isSelected={selectedTemplate === templateName}
                    onSelect={setSelectedTemplate}
                  />
                ))}
              </div>
              <div
                className={styles.imageContainer}
                onClick={() => {
                  track(TrackingEvent.TemplateClick, {[TrackingProperty.Template]: selectedTemplate});
                  handleTemplateSelection();
                }}
              >
                <img className={styles.ctaImage} src={templateAssets[selectedTemplate]} width='984' height='448' />
                <Button className={styles.imageHoverCta}>Try this Template </Button>
              </div>
            </div>
            <div className={styles.exploreFooterButton}>
              <Button
                onClick={() => {
                  track(TrackingEvent.Finish);
                  modalManager.closeModal();
                }}
              >
                {`Finish and close`}
              </Button>
            </div>
          </ScrollableElement>
        </div>
      </Content>
    </>
  );
};
